'use client';

import { useQuery } from '@tanstack/react-query';
import { settingAPI, postAPI } from '@/lib/api';
import { getImageUrl } from '@/lib/utils';
import Link from 'next/link';

export default function NewsCenter() {
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingAPI.getAll(),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: postsData, isLoading } = useQuery({
    queryKey: ['posts-news', { status: 'published', limit: 6 }],
    queryFn: () => postAPI.getList({ status: 'published', limit: 6 }),
  });

  // settingAPI.getAll() 现在直接返回 Settings 对象
  const settings = settingsData || {};
  const newsCenterTitle = settings.news_center_title || 'News Center';
  const newsCenterDescription = settings.news_center_description || 'Learn more about Sany Heavy Industry News, Industry News, Service Support, etc.';
  
  // 过滤出有图片的posts，并只取前2个
  const allPosts = postsData?.data || [];
  const postsWithImages = allPosts.filter((post: any) => post.featuredImage).slice(0, 2);

  return (
    <section 
      className="elementor-section elementor-top-section elementor-element elementor-element-4860806 elementor-section-boxed elementor-section-height-default elementor-section-height-default" 
      data-id="4860806" 
      data-element_type="section" 
      data-settings='{"background_background":"classic"}'
      style={{ backgroundColor: '#FFFFFF', background: '#FFFFFF', padding: '50px 0' }}
    >
      <div className="elementor-container elementor-column-gap-default" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box', width: '100%' }}>
        <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-01d2b44" data-id="01d2b44" data-element_type="column" style={{ paddingLeft: '0', paddingRight: '0' }}>
          <div className="elementor-widget-wrap elementor-element-populated">
            {/* 标题 */}
            <div className="elementor-element elementor-element-d133593 elementor-widget elementor-widget-heading" data-id="d133593" data-element_type="widget" data-widget_type="heading.default">
              <div className="elementor-widget-container">
                <h3 className="elementor-heading-title elementor-size-default">{newsCenterTitle}</h3>
              </div>
            </div>
            {/* 描述 */}
            <div className="elementor-element elementor-element-af99aef elementor-widget elementor-widget-heading" data-id="af99aef" data-element_type="widget" data-widget_type="heading.default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
              <div className="elementor-widget-container" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                <p className="elementor-heading-title elementor-size-default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>{newsCenterDescription}</p>
              </div>
            </div>

            {/* 内层section：两列布局 - 新闻网格 + 右侧菜单 */}
            <section className="elementor-section elementor-inner-section elementor-element elementor-element-e5eef33 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="e5eef33" data-element_type="section">
              <div className="elementor-container elementor-column-gap-no">
                {/* 左列：新闻网格 - 60%宽度 */}
                <div className="elementor-column elementor-col-60 elementor-inner-column elementor-element elementor-element-6924444" data-id="6924444" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-a307d44 elementor-widget elementor-widget-ucaddon_post_grid" data-id="a307d44" data-element_type="widget" data-widget_type="ucaddon_post_grid.default">
                      <div className="elementor-widget-container">
                        {isLoading ? (
                          <div className="text-center py-8">加载中...</div>
                        ) : postsWithImages.length > 0 ? (
                          <div className="uc_post_grid_style_one" id="uc_post_grid_elementor_a307d44">
                            <div className="uc_post_grid_style_one_wrap ue_post_grid uc-items-wrapper">
                              {postsWithImages.map((post: any) => (
                                <div key={post.id} className="uc_post_grid_style_one_item ue_post_grid_item ue-item">
                                  <Link href={`/post/${post.slug}`} className="uc_post_grid_style_one_image">
                                    <div 
                                      className="uc_post_image" 
                                      style={{
                                        backgroundImage: `url(${getImageUrl(post.featuredImage)})`,
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'cover',
                                        position: 'relative',
                                        width: '100%',
                                        height: 0,
                                        paddingBottom: '80%', // 5:4 宽高比 (高度/宽度 = 4/5 = 0.8 = 80%)
                                        aspectRatio: '5 / 4', // 使用 aspect-ratio 确保 5:4 比例
                                        display: 'block',
                                        boxSizing: 'border-box',
                                      }}
                                    >
                                      <div className="uc_post_image_overlay"></div>
                                    </div>
                                  </Link>
                                  <div className="uc_content">
                                    <div>
                                      <div className="uc_post_title">
                                        <Link href={`/post/${post.slug}`}>
                                          <div>{post.title}</div>
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">暂无新闻</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右列：News Center 菜单 - 40%宽度 */}
                <div className="elementor-column elementor-col-40 elementor-inner-column elementor-element elementor-element-b9c1ede" data-id="b9c1ede" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-99e8265 elementor-widget elementor-widget-heading" data-id="99e8265" data-element_type="widget" data-widget_type="heading.default">
                      <div className="elementor-widget-container">
                        <h4 className="elementor-heading-title elementor-size-default">News Center</h4>
                      </div>
                    </div>
                    <div className="elementor-element elementor-element-61c4648 elementor-nav-menu--dropdown-none elementor-widget elementor-widget-nav-menu" data-id="61c4648" data-element_type="widget" data-widget_type="nav-menu.default">
                      <div className="elementor-widget-container">
                        <nav aria-label="Menu" className="elementor-nav-menu--main elementor-nav-menu__container elementor-nav-menu--layout-vertical e--pointer-text e--animation-none">
                          <ul className="elementor-nav-menu sm-vertical">
                            <li className="menu-item">
                              <Link href="/news" className="elementor-item menu-link">News</Link>
                            </li>
                            <li className="menu-item">
                              <Link href="/news/sany-news" className="elementor-item menu-link">Sany News</Link>
                            </li>
                            <li className="menu-item">
                              <Link href="/news/press-release" className="elementor-item menu-link">Press Release</Link>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
}

